import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type SplitHeroV2Data } from '@/features/cms/components/CMS/types'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { AddressWrapper } from '@/features/cms/components/CMS/components/AddressCheck'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { SingleEntryForm } from '@/features/cms/components/CMS/components/SingleEntryForm'
import { spacing } from '@/features/shared/theme/tokens'
import { Display, Body } from '@shipt/design-system-typography'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { classNames } from '@/features/shared/utils/classNames'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Stack } from '@shipt/design-system-layouts'

/**
 * This component corresponds to the `split_hero_v2` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/split_hero_v2
 * - **Production** - https://cms.shipt.com/content-type/pool/split_hero_v2
 */
export const SplitHeroV2 = ({
  content_type_id,
  id,
  data: {
    heading,
    heading_color = '#000000',
    subheading,
    subheading_rich_text,
    subheading_color = '#000000',
    image,
    hide_mobile_image,
    image_placement = 'Desktop: Right / Mobile: Bottom',
    background_color = '#fff',
    call_to_action,
    references,
    single_entry_form = [],
    partner_logo,
  },
  className,
}: SplitHeroV2Data & { className?: string }) => {
  const url = useUtmUrl(call_to_action?.url || '')
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const handleButtonClick = () => {
    trackCMSElementClicked({
      type: 'button',
      content: call_to_action?.text,
    })
  }

  try {
    return (
      <SectionLayoutContainer
        $background_color={background_color}
        className={classNames(background_color && 'with-background', className)}
        {...getCMSWrapperProps({ content_type_id, id })}
      >
        <Container
          spacing={{ base: 'xxl', xl: '3xl' }}
          align="center"
          direction={{
            base: image_placement === 'Desktop: Left / Mobile: Top' ? 'column-reverse' : 'column',
            md: image_placement === 'Desktop: Left / Mobile: Top' ? 'row-reverse' : 'row',
          }}
        >
          <Split>
            <Content>
              {!!partner_logo?.src && (
                <PartnerLogoWrapper>
                  <Image
                    src={partner_logo.src}
                    alt={partner_logo.alt}
                    style={{ objectFit: 'contain' }}
                    width={partner_logo.width}
                    height={partner_logo.height}
                  />
                </PartnerLogoWrapper>
              )}
              <Heading as="h1" $heading_color={heading_color} size="sm" align="center">
                {heading}
              </Heading>
              {!!subheading_rich_text && (
                <Subheading as="div" $subheading_color={subheading_color} align="center">
                  {subheading_rich_text.elements.map(renderElement)}
                </Subheading>
              )}
              {!!subheading && !subheading_rich_text && (
                <Subheading $subheading_color={subheading_color} align="center">
                  {subheading}
                </Subheading>
              )}
              <ReferenceRenderer references={references} />
              {!!call_to_action && !references && !!url && (
                <ButtonLink href={url} concept="accent" onClick={handleButtonClick}>
                  {call_to_action.text}
                </ButtonLink>
              )}
              {!!single_entry_form[0] && <SingleEntryForm data={single_entry_form[0]} />}
            </Content>
          </Split>
          <Split $hiddenOnMobile={hide_mobile_image}>
            <Content>
              {!!image.src && (
                <HeroImage
                  src={image.src}
                  alt={image.alt}
                  style={{ width: '100%', height: 'auto' }}
                  width={image.width}
                  height={image.height}
                  preload
                />
              )}
            </Content>
          </Split>
        </Container>
      </SectionLayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const SectionLayoutContainer = styled(GridLayoutContainer)<{
  $background_color: string | null
}>`
  background-color: ${({ $background_color }) => $background_color};
`

const Container = styled(Stack)`
  border-radius: 8px;
`

const Split = styled.div<{ $hiddenOnMobile?: boolean }>`
  width: 100%;
  height: 100%;

  @media ${screenSizes.max_mobile} {
    display: ${({ $hiddenOnMobile }) => ($hiddenOnMobile ? 'none' : null)};
  }
`

const HeroImage = styled(Image)`
  width: 100%;
  border-radius: 8px;
`

const PartnerLogoWrapper = styled.div`
  max-width: 12rem;
  max-height: 9rem;
  margin: ${spacing(0, 'auto', 'lg')};

  @media ${screenSizes.tablet} {
    margin-right: ${spacing(0)};
    margin-bottom: ${spacing('xl')};
    margin-left: ${spacing(0)};
  }
`

const Heading = styled(Display)<{ $heading_color: string | null }>`
  margin-bottom: ${spacing(20)};
  color: ${({ $heading_color }) => $heading_color};

  @media ${screenSizes.tablet} {
    text-align: start;
    padding-right: ${spacing(20)};
    font-size: 2.5rem;
  }

  @media ${screenSizes.lgDesktop} {
    font-size: 3rem;
    padding-bottom: ${spacing('xl')};
    line-height: 3.3rem;
  }
`

const Subheading = styled(Body)<{ $subheading_color: string | null }>`
  margin-bottom: ${spacing('lg')};

  *,
  & {
    color: ${({ $subheading_color }) => $subheading_color};
  }

  @media ${screenSizes.tablet} {
    text-align: start;
    padding-right: ${spacing(20)};
  }

  b {
    font-weight: 600;
  }

  @media ${screenSizes.lgDesktop} {
    padding-bottom: ${spacing('xl')};
  }
`

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media ${screenSizes.tablet} {
    align-items: flex-start;
  }

  ${AddressWrapper} {
    width: 100%;
  }
`
